import type { FC } from "react"
import React, { useEffect, useState } from "react"
import { Stack, Text, Button, Box, useTheme, useToast } from "@chakra-ui/react"
import { WarningTwoIcon } from "@chakra-ui/icons"
import { useSwitchNetwork } from "wagmi"

import { getChainIdParams } from "web3/helpers/chainId"
import { useAddNetwork } from "web3/hooks/useAddNetwork"
import { useChainReference } from "web3/hooks/useChainReference"

type Props = {
  requiredChainId: string
  chainReferenceName?: string
  hasMessage?: boolean
}

const SwitchNetwork: FC<Props> = ({
  chainReferenceName,
  requiredChainId,
  hasMessage = true,
}) => {
  const [isNetworkSelectionLoading, setIsNetworkSelectionLoading] =
    useState(false)
  const [isRejected, setIsRejected] = useState(false)
  const theme = useTheme()
  const chainReference = useChainReference()
  const toast = useToast()

  const { reference: requiredChainReference } =
    getChainIdParams(requiredChainId)

  const isCorrectNetwork = requiredChainReference === chainReference

  useEffect(() => {
    if (isCorrectNetwork) {
      setIsNetworkSelectionLoading(false)
    }
  }, [isCorrectNetwork])

  const { switchNetwork, error } = useSwitchNetwork()
  const { addNetwork, isRejected: isAddNetworkRejected } = useAddNetwork()
  const canSwitchNetwork = Boolean(switchNetwork)

  const hasUserRejection = isRejected || isAddNetworkRejected

  const handleSwitchNetwork = () => {
    setIsNetworkSelectionLoading(true)

    switchNetwork?.(requiredChainReference)
  }

  useEffect(() => {
    if (error?.name === `UserRejectedRequestError`) {
      setIsRejected(true)
    }

    if (error?.name === `AddChainError`) {
      // this can be an enum
      addNetwork(requiredChainId)

      return
    }

    if (error?.name) {
      toast({
        status: "error",
        title: "Error",
        description:
          "We couldn't switch to this network. Please, try to do it manually",
      })
    }
  }, [addNetwork, error, requiredChainId, toast])

  return (
    <Stack
      align="center"
      bgColor={hasMessage ? theme.colors.red[100] : "none"}
      direction={{ base: "column", sm: "row" }}
      justify={hasMessage ? "space-between" : "center"}
      mt={4}
      px={3}
      py={4}
      width="full"
    >
      {hasMessage && (
        <Stack align="center" direction="row">
          <WarningTwoIcon color={theme.colors.red[600]} fontSize={22} />
          {chainReferenceName ? (
            <Text color={theme.colors.red[600]} fontWeight="bold">
              You are connected to the wrong network, please switch to{" "}
              {chainReferenceName}
              {/* use network tags */}
            </Text>
          ) : (
            <Text color={theme.colors.red[600]} fontWeight="bold">
              You are connected to the wrong network
            </Text>
          )}
        </Stack>
      )}
      {requiredChainId && canSwitchNetwork && (
        <Box pt={{ base: 2, sm: 0 }} width={{ base: "full", sm: "auto" }}>
          <Stack>
            <Button
              isLoading={isNetworkSelectionLoading && !hasUserRejection}
              size="lg"
              variant="primary"
              width="100%"
              onClick={handleSwitchNetwork}
            >
              Switch Network
            </Button>
            {hasUserRejection && (
              <Text color="red.500" textAlign="center">
                You have rejected the transaction.
              </Text>
            )}
          </Stack>
        </Box>
      )}
    </Stack>
  )
}

export default SwitchNetwork
